import { Injectable } from '@angular/core';
import mqtt from "mqtt";
import {environment} from "../../environments/environment";
import {BehaviorSubject, Subject} from "rxjs";
import {Store} from "@ngrx/store";
import {AppState} from "../store";
import {getActiveSchoolClassIdStudent} from "../store/selectors/student.selector";
import {LargeMessageService} from "./large-message.service";
import {LargeMessageTheme} from "../enums/large-message-theme";
import {LargeMessageInput} from "../interfaces/large-message-input";
import {TrophyRepositoryService} from "./trophy-repository.service";
import {QuestsHelperService} from "./quests-helper.service";
import {RookieDayReloadPayload} from "../interfaces/rookieday/rookieday-reload";

@Injectable({
  providedIn: 'root'
})
export class MqttClientService {

  client;
  schoolClassTopic = "";
  gameSessionTimeSubject = new BehaviorSubject<string>("");
  gameSessionPauseSubject = new BehaviorSubject<boolean>(false);
  gameSessionEndSubject = new BehaviorSubject<boolean>(false);

  rookieRemainingSlots = new BehaviorSubject<any>([]);

  rookieDayReload = new Subject<RookieDayReloadPayload>();

  private readonly rookieDayTopic = "rookieday";
  private subscribedTopics = new Set<string>();

  constructor(
    private store: Store<AppState>,
    private largeMessageService: LargeMessageService,
    private trophyRepositoryService: TrophyRepositoryService,
    private questsHelperService: QuestsHelperService
  ) {
    this.client = mqtt.connect(environment.mqttBroker);

    // Registered once - the handler dispatches by topic, so it must not be added again per subscribed topic
    this.listenToMessages();

    // Get Active School Class from State and connect to MQTT Topic
    this.store.select(getActiveSchoolClassIdStudent()).subscribe(response => {
      if (response) {
        this.schoolClassTopic = 'sc-' + response;
        this.connectToTopic(this.schoolClassTopic);
      }
    });


    this.connectToTopic("rookie");

  }

  connectToTopic(schoolClassTopic: string) {
    if (this.subscribedTopics.has(schoolClassTopic)) {
      return;
    }
    this.subscribedTopics.add(schoolClassTopic);

    this.client.subscribe(schoolClassTopic, (err) => {
      if (!err) {
        console.log("Connected to " + schoolClassTopic);
      } else {
        this.subscribedTopics.delete(schoolClassTopic);
        console.error(err);
      }
    });
  }

  /** Only the RookieDay appointment pages need this topic, so it's not subscribed globally. */
  connectToRookieDayTopic() {
    this.connectToTopic(this.rookieDayTopic);
  }

  listenToMessages() {
    this.client.on('message', (topic, message) => {
      // message is Buffer
      switch (topic) {
        case this.schoolClassTopic:
          this.handleSchoolClassMessage(message);
          break;
        case "rookie":
          this.handleRookieClassMessage(message);
          break;
        case this.rookieDayTopic:
          this.handleRookieDayMessage(message);
          break;
        default:
          console.warn('Could not resolve message in topic ' + topic);
      }
    });
  }

  handleSchoolClassMessage(message: Buffer) {
    try {

      // Check if JSON was sent
      const messageObject = JSON.parse(message.toString());

      if(Object.hasOwn(messageObject, 'type')) {
        switch (messageObject.type) {
          case 'GAME_TIMER_START':
            this.setGameSessionTime(messageObject.payload.gametimer.endAt);
            break;
          case 'GAME_TIMER_PAUSE':
            this.pauseGameSessionTime();
            break;
          case 'GAME_TIMER_RESUME':
            this.resumeGameSessionTime(messageObject.payload.gametimer.endAt);
            break;
          case 'GAME_TIMER_STOP':
            this.endGameSessionTime();
            break;
          case 'GAME_TIMER_EXTEND':
            this.extendGameSessionTime(messageObject.payload.gametimer.endAt);
            break;
          case 'TROPHY_UNLOCK':
            this.showTrophyOverlay(messageObject.payload);
            break;
          case 'SCHOOLCLASS_POINTS':
            console.log(messageObject);
            // this.questsHelperService.showSuccessLargeMessageByQuestTaskId(messageObject.payload.id);
            break;
          case 'QUEST_UNLOCK':
            this.questsHelperService.showLevelAchievedNotification();
            break;
          case 'QUEST_TASK_FINISHED':
            // this.questsHelperService.showSuccessLargeMessageByQuestTaskId(messageObject.payload.id);
            break;
          case 'QUEST_FINISHED':
            console.log('QUEST_FINISHED', messageObject);
            this.questsHelperService.showSuccessLargeMessageByQuestTaskId(messageObject.payload.id);
            break;
          default:
            console.warn('Could not resolve message type ' + messageObject.type);

        }
      }


      return true;

    } catch (e) {
      // Could not parse JSON
      // console.log(e);
      console.warn(message.toString());
      return false;
    }
  }

  handleRookieClassMessage(message: Buffer) {
    try {

      // Check if JSON was sent
      const messageObject = JSON.parse(message.toString());

      if(Object.hasOwn(messageObject, 'type')) {
        switch (messageObject.type) {
          case 'Remaining_Slots_Update':
            console.log("messageObject.payload", messageObject.payload);
            this.rookieRemainingSlots.next(messageObject.payload);
            break;
          default:
            console.warn('Could not resolve message type ' + messageObject.type);
        }
      }


      return true;

    } catch (e) {
      // Could not parse JSON
      // console.log(e);
      console.warn(message.toString());
      return false;
    }
  }

  handleRookieDayMessage(message: Buffer) {
    try {
      const messageObject = JSON.parse(message.toString());

      if (Object.hasOwn(messageObject, 'type')) {
        switch (messageObject.type) {
          case 'ROOKIEDAY_RELOAD':
            this.rookieDayReload.next(messageObject.payload);
            break;
          default:
            console.warn('Could not resolve message type ' + messageObject.type);
        }
      }

      return true;

    } catch (e) {
      // Could not parse JSON
      console.warn(message.toString());
      return false;
    }
  }

  sendMessage(message: string) {
    this.client.publish(environment.mqttTopic, message);
  }

  setGameSessionTime(state: string) {
    // console.log("Setting Game Session Time to " + state + " seconds.");
    this.gameSessionTimeSubject.next(state);
  }

  pauseGameSessionTime() {
    // console.log("Pausing Game Session Time.");
    this.gameSessionPauseSubject.next(true);
  }
  resumeGameSessionTime(state: string) {
    // console.log("Resuming Game Session Time.");
    this.gameSessionTimeSubject.next(state);
    this.gameSessionPauseSubject.next(false);
  }
  endGameSessionTime() {
    // console.log("End Game Session Time.");
    this.gameSessionEndSubject.next(false);
  }
  extendGameSessionTime(state: string) {
    // console.log("Extend Game Session Time.", state);
    this.gameSessionTimeSubject.next(state);
  }

  showTrophyOverlay(payload: any) {

    this.largeMessageService.showLargeMessage({
      theme: LargeMessageTheme.GOLD,
      headline: "Super, neue Trophäe <em>gesammelt!</em>",
      textTitle: payload.trophyname,
      text: payload.trophydescription,
      primaryButtonLabel: "Ok",
      primaryButtonCallback: () => {},
      mainIcon: this.trophyRepositoryService.getTrophySuccessIconById(payload.trophyid),
      lottiePath: "/assets/placeholder.lottie",
      lottieLargeScale: true
    })

  }

}
